import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Logger } from 'src/utils/logger';
import {
  SavedRepurposeContent,
  VideoAnalysisService,
  VideoAnalysisTriggerRequest,
} from 'src/services/video-analysis/video-analysis.service';

// Module-scope dedupe: when multiple `useContentState` instances mount with the
// same mediaId at the same time, share one in-flight network request.
const inFlightLoads: Map<string, Promise<SavedRepurposeContent>> = new Map();

// Treat empty / whitespace-only strings as absent so the BE returning {blog:''}
// (partial generation, soft-delete, etc.) never wipes a user's cached content.
const isPresent = (value?: string): value is string =>
  Boolean(value && value.trim().length > 0);

// Local cache wins when both are present: a user's edit (saved only to
// localStorage today) must survive a refresh that re-hydrates from the server.
// Server still fills any gap, so a fresh browser hydrates correctly.
const pickPreferLocal = (local?: string, remote?: string): string | undefined => {
  if (isPresent(local)) return local;
  if (isPresent(remote)) return remote;
  return undefined;
};

interface ContentState {
  blog?: string;
  ig_captions?: string;
  email_text?: string;
}

interface ContentStore {
  content: Record<string, ContentState>; // keyed by mediaId
  loading: Record<string, boolean>; // loading states per mediaId
  errors: Record<string, string | null>; // errors per mediaId
}

export const useContentState = () => {
  const [store, setStore] = useState<ContentStore>({
    content: {},
    loading: {},
    errors: {}
  });

  // Per-instance mounted flag: gates post-await setters so a hook whose host
  // component unmounted mid-fetch doesn't update stale React state. The
  // shared in-flight Promise keeps running because a sibling instance may
  // still be waiting on it.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Scoped logger is identity-stable across renders so it doesn't bust the
  // useCallback dependency arrays below.
  const logger = useMemo(() => Logger.createScopedLogger('useContentState'), []);

  // Listen for content changes from other components
  useEffect(() => {
    const handleContentChange = (event: CustomEvent) => {
      const { mediaId, contentType, content } = event.detail;
      setStore(prev => ({
        ...prev,
        content: {
          ...prev.content,
          [mediaId]: {
            ...prev.content[mediaId],
            [contentType]: content
          }
        }
      }));
    };

    window.addEventListener('contentStateChanged', handleContentChange as EventListener);
    return () => {
      window.removeEventListener('contentStateChanged', handleContentChange as EventListener);
    };
  }, []);

  // Get content for specific media and type
  const getContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']): string | undefined => {
    return store.content[mediaId]?.[contentType];
  }, [store.content]);

  // Check if content exists for specific media and type
  const hasContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']): boolean => {
    const content = store.content[mediaId]?.[contentType];
    return !!content && content.trim().length > 0;
  }, [store.content]);

  // Set content for specific media and type
  const setContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type'], content: string) => {
    setStore(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [mediaId]: {
          ...prev.content[mediaId],
          [contentType]: content
        }
      },
      errors: {
        ...prev.errors,
        [mediaId]: null // Clear any previous errors
      }
    }));
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('contentStateChanged', { 
      detail: { mediaId, contentType, content } 
    }));
    
    logger.info(`Content set for ${mediaId}:${contentType}`, { contentLength: content.length });
  }, [logger]);

  // Clear content for specific media and type
  const clearContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']) => {
    setStore(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [mediaId]: {
          ...prev.content[mediaId],
          [contentType]: undefined
        }
      }
    }));
    
    logger.info(`Content cleared for ${mediaId}:${contentType}`);
  }, [logger]);

  // Set loading state for specific media
  const setLoading = useCallback((mediaId: string, loading: boolean) => {
    setStore(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        [mediaId]: loading
      }
    }));
  }, []);

  // Set error for specific media
  const setError = useCallback((mediaId: string, error: string | null) => {
    setStore(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [mediaId]: error
      }
    }));
    
    if (error) {
      logger.error(`Content error for ${mediaId}`, error);
    }
  }, [logger]);

  // Get loading state for specific media
  const isLoading = useCallback((mediaId: string): boolean => {
    return store.loading[mediaId] || false;
  }, [store.loading]);

  // Get error for specific media
  const getError = useCallback((mediaId: string): string | null => {
    return store.errors[mediaId] || null;
  }, [store.errors]);

  // Hydrate from localStorage as a fallback / fast path when API is unavailable
  // or returns nothing. Returns whatever was found locally.
  const readLocalStorage = useCallback((mediaId: string): ContentState => {
    const storageKey = `content_${mediaId}`;
    const storedContent = localStorage.getItem(storageKey);
    if (!storedContent) return {};
    try {
      return JSON.parse(storedContent) as ContentState;
    } catch (parseError) {
      logger.warn(`Failed to parse stored content for ${mediaId}`, parseError);
      return {};
    }
  }, [logger]);

  const writeLocalStorage = useCallback((mediaId: string, content: ContentState) => {
    const storageKey = `content_${mediaId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(content));
    } catch (writeError) {
      logger.warn(`Failed to write content cache for ${mediaId}`, writeError);
    }
  }, [logger]);

  // Load saved content for a media from the backend. Paints the localStorage
  // cache synchronously so the green tick is instant for repeat visitors, then
  // overlays the server result so a fresh browser also hydrates correctly.
  const loadContent = useCallback(async (mediaId: string) => {
    if (!mediaId) return;

    // Synchronous fast path — restores the previous localStorage-only UX so
    // existing users see no flicker.
    const local = readLocalStorage(mediaId);
    if (local && Object.keys(local).length > 0) {
      setStore(prev => ({
        ...prev,
        content: {
          ...prev.content,
          [mediaId]: { ...prev.content[mediaId], ...local },
        },
      }));
    }

    setLoading(mediaId, true);
    setError(mediaId, null);

    // Dedupe simultaneous loads across hook instances.
    let pending = inFlightLoads.get(mediaId);
    if (!pending) {
      pending = VideoAnalysisService.getSavedByMedia(mediaId);
      inFlightLoads.set(mediaId, pending);
      pending.finally(() => {
        inFlightLoads.delete(mediaId);
      });
    }

    try {
      const remote = await pending;
      // Skip stale state updates if this instance unmounted mid-fetch.
      if (!isMountedRef.current) return;

      // Compute the merged value inside the setStore updater so concurrent
      // updates that arrived during the network round-trip (e.g. another
      // component dispatching `contentStateChanged`) are not clobbered.
      // Priority: existing in-memory > local cache snapshot > remote.
      let writtenSnapshot: ContentState = {};
      setStore(prev => {
        const existing: ContentState = prev.content[mediaId] || {};
        const merged: ContentState = {
          blog: pickPreferLocal(existing.blog, pickPreferLocal(local.blog, remote.blog)),
          ig_captions: pickPreferLocal(existing.ig_captions, pickPreferLocal(local.ig_captions, remote.ig_captions)),
          email_text: pickPreferLocal(existing.email_text, pickPreferLocal(local.email_text, remote.email_text)),
        };
        writtenSnapshot = merged;
        return {
          ...prev,
          content: {
            ...prev.content,
            [mediaId]: merged,
          },
        };
      });

      writeLocalStorage(mediaId, writtenSnapshot);
      logger.info(`Loaded saved content from API for ${mediaId}`, {
        types: Object.entries(writtenSnapshot)
          .filter(([, v]) => isPresent(v))
          .map(([k]) => k),
      });
    } catch (error) {
      // Local cache (if any) was already painted above; nothing else to do.
      logger.warn(`API hydration failed for ${mediaId}, kept localStorage fallback`, error);
    } finally {
      if (isMountedRef.current) {
        setLoading(mediaId, false);
      }
    }
  }, [logger, readLocalStorage, writeLocalStorage, setLoading, setError]);

  // Save content for media (placeholder for API integration)
  const saveContent = useCallback(async (mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type'], content: string) => {
    if (!mediaId || !content.trim()) return false;

    setLoading(mediaId, true);
    setError(mediaId, null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // await ContentAPI.saveContent(mediaId, contentType, content);

      const existingContent: ContentState = store.content[mediaId] || {};
      const updatedContent: ContentState = {
        ...existingContent,
        [contentType]: content,
      };

      writeLocalStorage(mediaId, updatedContent);
      setContent(mediaId, contentType, content);

      logger.info(`Saved content to localStorage for ${mediaId}:${contentType}`);
      return true;
    } catch (error) {
      logger.error(`Failed to save content for ${mediaId}:${contentType}`, error);
      setError(mediaId, 'Failed to save content');
      return false;
    } finally {
      setLoading(mediaId, false);
    }
  }, [logger, store.content, setContent, setLoading, setError, writeLocalStorage]);

  // Get all content types that have content for a media
  const getContentTypes = useCallback((mediaId: string): VideoAnalysisTriggerRequest['request_type'][] => {
    const mediaContent = store.content[mediaId];
    if (!mediaContent) return [];

    return Object.entries(mediaContent)
      .filter(([_, content]) => content && content.trim().length > 0)
      .map(([type, _]) => type as VideoAnalysisTriggerRequest['request_type']);
  }, [store.content]);

  return {
    // Content operations
    getContent,
    hasContent,
    setContent,
    clearContent,
    loadContent,
    saveContent,
    getContentTypes,
    
    // State queries
    isLoading,
    getError,
    
    // Raw store access (for debugging)
    store
  };
};
